/**
 * 批量采样安排 — 对齐 Ivan GetAllPickTasks
 * 数据：yn_xm + yn_xm_item（按监测项一行），非派遣单整表
 */
import api from '@/utils/api'
import { displayItemNames, displayMonitorPlace } from '@/utils/siteMonitorMap'

export const IVAN_INSPECT_SUB_TYPES = ['环境空气', '废气', '废水', '地下水', '土壤', '噪声']
export const IVAN_CYCLE_TYPES = ['月', '季', '半年', '年']

/** 采样车辆可选列表（车牌+车型） */
export const PICK_VEHICLE_PLATES = [
  '甘DFD1237奇瑞风云T9',
  '甘DFD1377奇瑞风云T9',
  '甘DFE4555长安猎手',
  '甘DFG8822五菱面包增程',
  '甘D20R78五菱面包',
  '甘AG8811本田飞度'
]

const PICK_XMZT = new Set([
  'pending', 'dispatched', 'sampling', 'doing', 'handover',
  'plan_ready', 'audit_pass', 'pending_audit', 'lab_ready'
])

const OVERRIDE_KEY = 'yn_pick_batch_overrides_v2'
const OVERRIDE_KEY_LEGACY = 'yn_pick_batch_overrides'

function loadOverrides() {
  try {
    const raw = localStorage.getItem(OVERRIDE_KEY)
    if (raw) return JSON.parse(raw) || {}
    // 旧版按 item-id 存，库清空后 ID 重用会串项目；丢弃不迁移
    try { localStorage.removeItem(OVERRIDE_KEY_LEGACY) } catch (e) { /* ignore */ }
    return {}
  } catch (e) {
    return {}
  }
}

/** 供面板读安排覆盖（孤儿行回填仪器/车牌） */
export function loadPickOverrides() {
  return loadOverrides()
}

/** 清空本机安排缓存（验收/清库后用） */
export function clearPickOverrides() {
  try {
    localStorage.removeItem(OVERRIDE_KEY)
    localStorage.removeItem(OVERRIDE_KEY_LEGACY)
  } catch (e) { /* ignore */ }
}

function saveOverrides(map) {
  try {
    localStorage.setItem(OVERRIDE_KEY, JSON.stringify(map))
  } catch (e) { /* ignore */ }
}

/** 覆盖键必须带项目号，避免 truncate 后 item 自增 id 撞车 */
export function pickOverrideKey(xmbh, rowId) {
  const tid = String(rowId || '').trim()
  const x = String(xmbh || '').trim()
  if (!tid) return ''
  return x ? `${x}::${tid}` : tid
}

function getOverride(map, xmbh, rowId) {
  if (!map || !rowId) return null
  const k = pickOverrideKey(xmbh, rowId)
  if (k && map[k]) return map[k]
  // 兼容同一次会话里只写了 row.id、但 ov 已带正确 xmbh 的情况
  const bare = map[rowId] || (String(rowId).indexOf('item-') === 0 ? null : map['item-' + rowId])
  if (bare && xmbh && String(bare.xmbh || '') === String(xmbh)) return bare
  return null
}

export function parseSj(row) {
  try {
    return JSON.parse(row.sj_json || '{}')
  } catch (e) {
    return {}
  }
}

/** Ivan 侧栏类型：把远诺 sub_type / basic_type 映射到展示名 */
export function mapInspectSubType(item) {
  const st = String((item && (item.sub_type || item.inspect_sub_type)) || '').trim()
  const bt = String((item && item.basic_type) || '').trim()
  if (IVAN_INSPECT_SUB_TYPES.includes(st)) return st
  if (/废水|污水/.test(st) || st === '水') return '废水'
  if (/地下/.test(st)) return '地下水'
  if (/土壤|沉积物/.test(st) || /土壤/.test(bt)) return '土壤'
  if (/噪声|厂界|生活/.test(st) || /噪声/.test(bt)) return '噪声'
  if (/有组织|烟气|废气/.test(st) || /烟气|废气/.test(bt)) return '废气'
  if (/无组织|环境空气|大气/.test(st) || /环境空气|大气/.test(bt)) return '环境空气'
  if (/气体/.test(st)) return '废气'
  return st || ''
}

function flowSuffix(xm) {
  const fm = xm.flow_model || ''
  if (fm === 'RoutineEntrustFlow') return '(自行监测)'
  if (fm === 'SelfSendEntrustFlow') return '(来样检测)'
  return '(委托检测)'
}

function caseNameOf(xm) {
  const name = String(xm.xmmc || xm.wtdw_mc || xm.xmbh || '采样任务').trim()
  const suf = flowSuffix(xm)
  if (name.includes('委托检测') || name.includes('自行监测') || name.includes('来样')) return name
  return name + suf
}

/** 采样项目：顿号改分号，对齐 Ivan */
function itemNamesIvan(item) {
  const raw = displayItemNames(item) || String(item.item_names || '').trim()
  if (!raw) return ''
  return raw.replace(/[、，,]/g, ';').replace(/;+/g, ';').replace(/^;|;$/g, '')
}

function placeIvan(item) {
  let place = displayMonitorPlace(item)
  if (!place || place === '待填写点位') {
    place = String(item.monitor_place || '').trim()
    if (!place) place = ''
  }
  const n = parseInt(item.place_count || 0, 10) || 0
  if (place && n > 1 && !/\(\d+个\)/.test(place)) {
    return `${place} (${n}个)`
  }
  return place
}

function freqIvan(item) {
  const unit = String(item.freq_unit || 'day')
  const days = parseInt(item.total_days || 1, 10) || 1
  const perDay = parseInt(item.freq_per_day || item.freq_count || 1, 10) || 1
  const periods = parseInt(item.periods_count || 1, 10) || 1
  const label = String(item.freq_label || '').trim()

  if (unit === 'period' || (label && /月|季|半年|年/.test(label))) {
    const cycle = IVAN_CYCLE_TYPES.includes(label) ? label : (label || '期')
    return `${cycle}，第1期，${perDay}次/期`
  }
  // 默认按天：第1天，共N天，M次/天
  if (days > 1) {
    return `第1天，共${days}天，${perDay}次/天`
  }
  return `第1天，共${days}天，${perDay}次/天`
}

function cycleIvan(item) {
  const label = String(item.freq_label || '').trim()
  if (IVAN_CYCLE_TYPES.includes(label)) return label
  if (/半年/.test(label)) return '半年'
  if (/季/.test(label)) return '季'
  if (/年/.test(label) && !/半年/.test(label)) return '年'
  if (/月/.test(label)) return '月'
  return ''
}

function formatPickDate(from, to) {
  if (!from) return ''
  const f = String(from).slice(0, 10)
  const t = String(to || from).slice(0, 10)
  const fmt = (d) => {
    if (!/^\d{4}-\d{2}-\d{2}/.test(d)) return d
    return `${d.slice(5, 7)}月${d.slice(8, 10)}日`
  }
  if (!t || t === f) return fmt(f)
  return `${fmt(f)}~${fmt(t)}`
}

function statusOf(xm, ov) {
  if (ov && ov.submitted) return '已提交'
  const zt = String(xm.xmzt || '').toLowerCase()
  if (zt === 'done') return '已完成'
  if (zt === 'sampling' || zt === 'doing') return '采样中'
  return '待安排采样'
}

function rowFromItem(xm, item, ov, bdslHint) {
  // 采样人员：只认「本监测项」已安排的人，不对整项目派遣单/项目负责人做兜底
  // （否则会把刘刚锋等项目负责人刷到每一行，和 Ivan 空态不一致）
  const charge = String((ov && ov.cyfzr) || (bdslHint && bdslHint.cyfzr) || item.sampler_xm || '').trim()
  const members = String((ov && ov.cyry) || (bdslHint && bdslHint.cyry) || '').trim()
  const from = (ov && ov.cyrq_from) || (bdslHint && bdslHint.cyrq_from) || ''
  const to = (ov && ov.cyrq_to) || (bdslHint && bdslHint.cyrq_to) || from
  const instruments = (ov && ov.cyyq) || (bdslHint && bdslHint.cyyq) || ''
  const vehiclePlate = (ov && ov.cph) || (bdslHint && bdslHint.cph) || ''

  return {
    id: 'item-' + item.id,
    itemId: item.id,
    xmbh: xm.xmbh,
    projectCode: xm.xmbh,
    xmmc: xm.xmmc || '',
    caseName: caseNameOf(xm),
    inspectSubType: mapInspectSubType(item),
    inspectItems: itemNamesIvan(item),
    monitorPlace: placeIvan(item),
    address: '',
    pickCharge: charge,
    pickMembers: members,
    pickStaff: members || charge,
    instruments,
    vehiclePlate,
    cycleType: cycleIvan(item),
    freqPickIndex: String(item.plan_period || ''),
    pickDateFrom: String(from).slice(0, 10),
    pickDateTo: String(to).slice(0, 10),
    pickDateLabel: formatPickDate(from, to),
    frequency: freqIvan(item),
    status: statusOf(xm, ov),
    contractNo: xm.htbh || '',
    remark: item.remark || '',
    selected: false,
    flowModel: xm.flow_model || '',
    rawXm: xm,
    rawItem: item,
    sj: {
      cyfzr: charge,
      cyry: members,
      cyrq_from: from,
      cyrq_to: to,
      cyyq: instruments,
      cph: vehiclePlate,
      item_id: item.id,
      xmbh: xm.xmbh
    }
  }
}

function bdslHintMap(bdslList) {
  const map = {}
  ;(bdslList || []).forEach(row => {
    const s = parseSj(row)
    // 仅绑定到具体监测项的派遣单才回填人员/日期（不按 xmbh 整项目套）
    const iid = s.item_id || s.xm_item_id
    if (!iid) return
    map['i:' + iid] = {
      cyfzr: String(s.cyfzr || '').trim(),
      cyry: String(s.cyry || '').trim(),
      cyrq_from: s.cyrq_from || s.cyrq || '',
      cyrq_to: s.cyrq_to || '',
      cyyq: s.cyyq || '',
      cph: s.cph || s.vehiclePlate || ''
    }
  })
  return map
}

/**
 * 拉取批量采样安排行（按监测项展开）
 */
export async function fetchPickRows() {
  const overrides = loadOverrides()
  const [xmRes, bdRes, asRes] = await Promise.all([
    api.get('yn_xm_list'),
    api.get('yn_bdsl_list').catch(() => ({ data: [] })),
    api.get('yn_pick_assignee_map').catch(() => ({ data: { map: {} } }))
  ])
  const xmList = Array.isArray(xmRes.data) ? xmRes.data : (xmRes.data && xmRes.data.list) || []
  const bdslList = Array.isArray(bdRes.data) ? bdRes.data : (bdRes.data && bdRes.data.list) || []
  const assigneeMap = (asRes.data && asRes.data.map) || {}
  const hints = bdslHintMap(bdslList)

  const projects = xmList.filter(p => {
    if (!p || !p.xmbh) return false
    if (p.entrust_status === 'draft' && (!p.xmzt || p.xmzt === 'new')) return false
    if (p.xmzt === 'done') return false
    if (PICK_XMZT.has(p.xmzt)) return true
    // 已提交委托但状态未标的，仍可能有监测项
    return p.entrust_status === 'submitted' || p.entrust_status === 'approved'
  })

  const rows = []
  const chunk = 8
  for (let i = 0; i < projects.length; i += chunk) {
    const part = projects.slice(i, i + chunk)
    const itemLists = await Promise.all(part.map(async (xm) => {
      try {
        const res = await api.get('yn_xm_item_list', { xmbh: xm.xmbh })
        const items = Array.isArray(res.data) ? res.data : (res.data && res.data.list) || []
        return { xm, items }
      } catch (e) {
        return { xm, items: [] }
      }
    }))
    itemLists.forEach(({ xm, items }) => {
      if (!items.length) return
      items.forEach(it => {
        const rowId = 'item-' + it.id
        const ov = getOverride(overrides, xm.xmbh, rowId)
        // 仅本 item 绑定的派遣单 / 覆盖；禁止 xmbh 级兜底
        const hint = hints['i:' + it.id] || null
        const asg = assigneeMap[rowId] || null
        // 服务端已安排：人员 / 日期 / 仪器 / 车牌 回填，避免只存在 rwfp、本机覆盖丢失后列空白
        let mergedOv = ov
        if (asg && (asg.charge || asg.people || asg.cyrq || asg.cyrq_from || asg.cyyq || asg.cph)) {
          const cyrq = asg.cyrq_from || asg.cyrq || ''
          mergedOv = {
            ...(ov || {}),
            cyfzr: (ov && ov.cyfzr) || asg.charge || asg.people || '',
            cyry: (ov && ov.cyry) || asg.members || '',
            cyfzr_id: (ov && ov.cyfzr_id) || (asg.ids && asg.ids[0]) || '',
            cyrq_from: (ov && ov.cyrq_from) || cyrq || '',
            cyrq_to: (ov && ov.cyrq_to) || cyrq || '',
            cyyq: (ov && ov.cyyq) || asg.cyyq || '',
            cph: (ov && ov.cph) || asg.cph || ''
          }
        }
        rows.push(rowFromItem(xm, it, mergedOv, hint))
      })
    })
  }

  // 无监测项但有派遣单的项目：补一行，避免列表空
  if (!rows.length && bdslList.length) {
    bdslList.slice(0, 80).forEach(b => {
      const s = parseSj(b)
      const xm = {
        xmbh: b.xmbh || s.xmbh,
        xmmc: b.xmmc || s.xmmc || s.rwmc,
        flow_model: s.flow_model || '',
        xmzt: 'dispatched',
        wtdw_dz: s.dz || ''
      }
      if (!xm.xmbh) return
      const fakeItem = {
        id: 'bdsl-' + b.id,
        sub_type: s.jclb || s.xcssfa || '',
        basic_type: '',
        item_names: s.jcxm || '',
        monitor_place: s.cdmc || s.pqdd || '',
        place_count: s.xcssfa_num || 1,
        total_days: 1,
        freq_per_day: 1,
        sampler_xm: s.cyry || ''
      }
      const fakeRowId = 'item-' + fakeItem.id
      rows.push(rowFromItem(xm, fakeItem, getOverride(overrides, xm.xmbh, fakeRowId), s))
    })
  }

  return rows
}

export function filterPickRows(rows, {
  keyword, dateFrom, dateTo, inspectSubType, cycleType, freqPickIndex
} = {}) {
  const kw = String(keyword || '').trim().toLowerCase()
  const sub = String(inspectSubType || '').trim()
  const cycle = String(cycleType || '').trim()
  const freq = String(freqPickIndex || '').trim()
  return rows.filter(r => {
    if (sub && r.inspectSubType !== sub) return false
    if (cycle && r.cycleType !== cycle) return false
    if (freq && !String(r.frequency).includes(`第${freq}期`) && String(r.freqPickIndex) !== freq) return false
    if (dateFrom && r.pickDateFrom && r.pickDateFrom < dateFrom) return false
    if (dateTo && r.pickDateFrom && r.pickDateFrom > dateTo) return false
    if (!kw) return true
    const hay = [r.caseName, r.xmbh, r.projectCode, r.xmmc, r.address, r.monitorPlace, r.inspectItems, r.pickStaff]
      .map(v => String(v || '').toLowerCase())
      .join(' ')
    return hay.indexOf(kw) >= 0
  })
}

export function uniqueSubTypes() {
  return IVAN_INSPECT_SUB_TYPES.slice()
}

export function uniqueCycleTypes() {
  return IVAN_CYCLE_TYPES.slice()
}

/**
 * 把 localStorage 安排覆盖回灌到行（softReload 竞态后保证仪器/车牌/人员可见）
 */
export function applyOverridesToRows(rows) {
  const overrides = loadOverrides()
  ;(rows || []).forEach(row => {
    if (!row || !row.id) return
    const ov = getOverride(overrides, row.xmbh, row.id)
      || (row.itemId != null ? getOverride(overrides, row.xmbh, 'item-' + row.itemId) : null)
      || null
    if (!ov) return
    if (ov.cyfzr !== undefined && ov.cyfzr !== '') row.pickCharge = ov.cyfzr
    if (ov.cyry !== undefined && ov.cyry !== '') {
      row.pickMembers = ov.cyry
      row.pickStaff = ov.cyry
    }
    if (ov.cyyq !== undefined) row.instruments = ov.cyyq
    if (ov.cph !== undefined) row.vehiclePlate = ov.cph
    if (ov.cyrq_from !== undefined && ov.cyrq_from) {
      row.pickDateFrom = String(ov.cyrq_from).slice(0, 10)
      row.pickDateTo = String(ov.cyrq_to || ov.cyrq_from).slice(0, 10)
      row.pickDateLabel = formatPickDate(row.pickDateFrom, row.pickDateTo)
    }
    row.sj = { ...(row.sj || {}), ...ov }
  })
  return rows
}

/** softReload 时保留勾选，避免弹窗确认时 selected 已空却仍提示成功 */
export function restorePickRowSelection(rows, selectedIds) {
  const ids = selectedIds instanceof Set
    ? selectedIds
    : new Set((selectedIds || []).map(String))
  ;(rows || []).forEach(r => {
    if (!r || !r.id) return
    r.selected = ids.has(String(r.id))
  })
  return rows
}

export function collectSelectedPickIds(rows) {
  return (rows || []).filter(r => r && r.selected && r.id).map(r => String(r.id))
}

/**
 * 已完成采样（审完）后安排字段不可再改：组员/日期/仪器/车牌
 */
export function isPickArrangeLocked(row) {
  if (!row) return false
  const ps = String(row.pickStatus || '').trim()
  if (ps === 'audited') return true
  const st = String(row.status || '').trim()
  return st === '已完成采样' || st === '已审核' || st === '样品交接'
}

/**
 * 提交前校验：组员、日期、仪器、车牌都必须有
 * @returns {{ ok: boolean, message?: string, missing?: object[] }}
 */
export function validatePickArrangeReady(rows) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : []
  if (!list.length) {
    return { ok: false, message: '请勾选需要提交的采样任务' }
  }
  const lackPeople = []
  const lackDate = []
  const lackInst = []
  const lackPlate = []
  list.forEach((r, i) => {
    const label = String(r.monitorPlace || r.inspectSubType || r.id || `第${i + 1}条`).trim()
    const people = String(r.pickCharge || r.pickMembers || r.pickStaff || '').trim()
    const date = String(r.pickDateFrom || r.pickDateLabel || (r.sj && r.sj.cyrq_from) || '').trim()
    const inst = String(r.instruments || (r.sj && r.sj.cyyq) || '').trim()
    const plate = String(r.vehiclePlate || (r.sj && r.sj.cph) || '').trim()
    if (!people || people === '—') lackPeople.push(label)
    if (!date || date === '—') lackDate.push(label)
    if (!inst || inst === '—') lackInst.push(label)
    if (!plate || plate === '—') lackPlate.push(label)
  })
  const parts = []
  if (lackPeople.length) parts.push('采样组员')
  if (lackDate.length) parts.push('采样日期')
  if (lackInst.length) parts.push('采样仪器')
  if (lackPlate.length) parts.push('车辆安排（车牌号）')
  if (!parts.length) return { ok: true }
  return {
    ok: false,
    message: `请先设置：${parts.join('、')}（未齐不可提交）`,
    missing: { lackPeople, lackDate, lackInst, lackPlate }
  }
}

/**
 * 批量写入安排字段（localStorage + yn_pick_assign → App yn_rwfp）
 * 仪器/车牌额外写入 yn_pick_arrange_fields，供 135/现场表灌 Word
 * 不再依赖 135 喂移动端任务
 */
export function formatPickedInstruments(payload) {
  const names = String((payload && payload.names) || '')
    .split(/[;；]/)
    .map(s => s.trim())
    .filter(Boolean)
  const nos = String((payload && payload.nos) || '')
    .split(/[;；]/)
    .map(s => s.trim())
  return names.map((n, i) => {
    const code = nos[i] || ''
    if (code && code !== '—' && code !== '/') return `${n}(${code})`
    return n
  }).join(';')
}

export async function patchPickFields(row, patch) {
  const key = pickOverrideKey(row.xmbh, row.id) || row.id
  const overrides = loadOverrides()
  const prev = getOverride(overrides, row.xmbh, row.id) || overrides[key] || {}
  const next = { ...prev, ...patch, xmbh: row.xmbh || prev.xmbh || '' }
  overrides[key] = next
  // 清掉可能串项目的裸 item-id 键
  if (row.id && overrides[row.id] && String((overrides[row.id] || {}).xmbh || '') !== String(row.xmbh || '')) {
    delete overrides[row.id]
  }
  saveOverrides(overrides)

  row.sj = { ...row.sj, ...patch }
  if (patch.cyfzr !== undefined) row.pickCharge = patch.cyfzr
  if (patch.cyfzr_id !== undefined) row.cyfzrId = patch.cyfzr_id
  if (patch.cyry !== undefined) {
    row.pickMembers = patch.cyry
    row.pickStaff = patch.cyry
  }
  if (patch.cyry_ids !== undefined) row.cyryIds = patch.cyry_ids
  if (patch.cyyq !== undefined) row.instruments = patch.cyyq
  if (patch.cph !== undefined) row.vehiclePlate = patch.cph
  if (patch.cyrq_from !== undefined) {
    row.pickDateFrom = String(patch.cyrq_from).slice(0, 10)
    row.pickDateTo = String(patch.cyrq_to || patch.cyrq_from).slice(0, 10)
    row.pickDateLabel = formatPickDate(row.pickDateFrom, row.pickDateTo)
  }

  const touchedArrange =
    patch.cyyq !== undefined || patch.cph !== undefined

  // 仪器/车牌必须落库；失败不阻断本机展示（否则弹窗选完却整段抛错，UI/$set 都走不到）
  if (touchedArrange) {
    try {
      await syncPickArrangeFields(row, next)
    } catch (e) {
      console.error('[pickAdapter] syncPickArrangeFields', e)
    }
  }
}

/** PC 仪器/车牌 → yn_pick_record.data_json（供列表回读 + 135 灌表） */
export async function syncPickArrangeFields(row, ov) {
  const xmbh = String((row && row.xmbh) || (ov && ov.xmbh) || '').trim()
  const taskId = String((row && row.id) || '').trim()
  if (!xmbh || !taskId) return { ok: 0, error: '缺少 xmbh/taskId' }
  const patch = ov || getOverride(loadOverrides(), xmbh, taskId) || {}
  const body = {
    xmbh,
    taskId,
    title: (row && (row.caseName || row.xmmc)) || xmbh,
    inspectType: (row && row.inspectSubType) || '',
    place: (row && row.monitorPlace) || '',
    itemNames: (row && row.inspectItems) || ''
  }
  if (patch.cyyq !== undefined) body.cyyq = String(patch.cyyq || '')
  if (patch.cph !== undefined) body.cph = String(patch.cph || '')
  if (body.cyyq === undefined && body.cph === undefined) {
    return { ok: 0, error: '无安排字段' }
  }
  try {
    const res = await api.postJson('yn_pick_arrange_fields', body)
    return (res && res.data) || res || { ok: 0 }
  } catch (e) {
    return { ok: 0, error: (e && e.message) || '保存安排字段失败' }
  }
}

/** 把浏览器里已选的仪器/车牌全部刷到服务端（安排页打开时补同步） */
export async function flushPickArrangeToServer(rows) {
  const overrides = loadOverrides()
  const list = Array.isArray(rows) && rows.length
    ? rows
    : Object.keys(overrides).map(id => ({
      id: id.indexOf('::') >= 0 ? id.split('::').slice(1).join('::') : id,
      xmbh: (overrides[id] && overrides[id].xmbh) || (id.indexOf('::') >= 0 ? id.split('::')[0] : ''),
      caseName: '',
      inspectSubType: '',
      monitorPlace: '',
      inspectItems: ''
    }))
  let n = 0
  for (const row of list) {
    if (!row || !row.id) continue
    const ov = getOverride(overrides, row.xmbh, row.id) || overrides[row.id] || {}
    if (ov.cyyq === undefined && ov.cph === undefined) continue
    const xmbh = row.xmbh || ov.xmbh || ''
    if (!xmbh) continue
    const ret = await syncPickArrangeFields({ ...row, xmbh }, ov)
    if (ret && ret.ok) n += 1
  }
  return n
}

/** PC 安排 → App 任务（yn_rwfp + yn_pick_record） */
export async function syncPickAssignToApp(row, ov, submitted) {
  const patch = ov || getOverride(loadOverrides(), row.xmbh, row.id) || {}
  const cyfzr = String(patch.cyfzr || row.pickCharge || '').trim()
  const cyry = String(patch.cyry || row.pickMembers || '').trim()
  const cyfzrId = patch.cyfzr_id != null && patch.cyfzr_id !== ''
    ? String(patch.cyfzr_id)
    : (row.cyfzrId != null ? String(row.cyfzrId) : '')
  let cyryIds = patch.cyry_ids || row.cyryIds || []
  if (!Array.isArray(cyryIds)) cyryIds = cyryIds ? [cyryIds] : []
  if (!cyfzr && !cyry && !cyfzrId && !cyryIds.length) return null
  try {
    const raw = row.rawItem || {}
    const res = await api.postJson('yn_pick_assign', {
      xmbh: row.xmbh,
      taskId: row.id,
      title: row.caseName || row.xmmc || row.xmbh,
      inspectType: row.inspectSubType || '',
      place: row.monitorPlace || '',
      itemNames: row.inspectItems || '',
      cyrq_from: row.pickDateFrom || patch.cyrq_from || '',
      cyfzr,
      cyfzr_id: cyfzrId,
      cyry,
      cyry_ids: cyryIds,
      cyyq: patch.cyyq != null ? patch.cyyq : (row.instruments || ''),
      cph: patch.cph != null ? patch.cph : (row.vehiclePlate || ''),
      submitted: submitted ? 1 : 0,
      freq_label: String(raw.freq_label || row.frequency || '').trim(),
      freq_count: parseInt(raw.freq_count || 0, 10) || 0,
      freq_per_day: parseInt(raw.freq_per_day || 0, 10) || 0,
      freq_unit: String(raw.freq_unit || '').trim(),
      plan_period: parseInt(row.freqPickIndex || raw.plan_period || 0, 10) || 0,
      periodIndex: parseInt(row.freqPickIndex || raw.plan_period || 0, 10) || 0,
      sampleSeq: parseInt(patch.sampleSeq || row.sampleSeq || 0, 10) || 0
    })
    return res && res.data
  } catch (e) {
    return { ok: 0, error: (e && e.message) || '同步 App 失败' }
  }
}

export async function markPickSubmitted(rows) {
  const overrides = loadOverrides()
  const list = Array.isArray(rows) ? rows : []
  for (const r of list) {
    const key = pickOverrideKey(r.xmbh, r.id) || r.id
    const prev = getOverride(overrides, r.xmbh, r.id) || {}
    overrides[key] = { ...prev, submitted: 1, xmbh: r.xmbh || prev.xmbh || '' }
  }
  saveOverrides(overrides)
  const results = []
  for (const r of list) {
    const ov = getOverride(overrides, r.xmbh, r.id) || {}
    const ret = await syncPickAssignToApp(r, ov, true)
    // 提交时一并落库仪器/车牌，避免只留在本机
    if (ov.cyyq !== undefined || ov.cph !== undefined) {
      await syncPickArrangeFields(r, ov)
    }
    results.push(ret)
  }
  return results
}

/** 旧 bdsl 路径兼容（采样日历等） */
export function normalizePickRow(row) {
  const s = parseSj(row)
  const xm = {
    xmbh: row.xmbh || s.xmbh,
    xmmc: row.xmmc || s.xmmc || s.rwmc,
    flow_model: s.flow_model || '',
    xmzt: row.bdzt || 'dispatched',
    wtdw_dz: s.dz || ''
  }
  const item = {
    id: row.id,
    sub_type: s.jclb || s.xcssfa || '',
    item_names: s.jcxm || '',
    monitor_place: s.cdmc || s.pqdd || '',
    place_count: s.xcssfa_num || 1,
    total_days: 1,
    freq_per_day: 1,
    sampler_xm: s.cyry || ''
  }
  return rowFromItem(xm, item, null, s)
}
